import React, { useMemo } from 'react';
import type { Cidade, Escola } from '../types';
import { calcTotal } from '../types';
import {
  UnifiedMapView,
  type MapDataPoint,
} from '../../developer-cockpit/components/sim-setup/UnifiedMapView';

export interface ResumoStepProps {
  escolas: Escola[];
  cidade: Cidade;
  resumo: ReturnType<typeof calcTotal>;
  onLaunch: () => void;
}

export function ResumoStep({ escolas, cidade, resumo, onLaunch }: ResumoStepProps) {
  const items = [
    { icon:'👨‍🎓', label:'Alunos',        value:resumo.alunos,   color:'#c4b5fd' },
    { icon:'👨‍🏫', label:'Professores',    value:resumo.profs,    color:'#86efac' },
    { icon:'👨‍👩‍👧', label:'Responsáveis',  value:resumo.resps,    color:'#fcd34d' },
    { icon:'🏫',  label:'Equipe Admin',    value:resumo.equipe,   color:'#93c5fd' },
    { icon:'📚',  label:'Turmas',          value:resumo.turmas,   color:'#f9a8d4' },
    { icon:'📶',  label:'Tags RFID',       value:resumo.tags,     color:'#6ee7b7' },
    { icon:'📡',  label:'Leitores RFID',   value:resumo.leitores, color:'#94a3b8' },
  ];

  // Monta pontos do mapa: escolas com GPS + centro da cidade como fallback
  const mapPoints = useMemo((): MapDataPoint[] => {
    const pts: MapDataPoint[] = [];

    // 1. Escolas com coordenadas reais (INEP)
    escolas.forEach(e => {
      if (e.lat && e.lng && isFinite(e.lat) && isFinite(e.lng)) {
        pts.push({
          id: e.id,
          type: 'SCHOOL',
          lat: e.lat,
          lng: e.lng,
          label: e.nome,
          sublabel: `${e.alunos.toLocaleString('pt-BR')} alunos · ${e.rede}`,
        });
      }
    });

    // 2. Se nenhuma escola tem GPS, usa o centro da cidade como referência única
    if (pts.length === 0 && cidade.lat && cidade.lng) {
      pts.push({
        id: 'cidade-center',
        type: 'SCHOOL',
        lat: cidade.lat,
        lng: cidade.lng,
        label: cidade.nome,
        sublabel: `${escolas.length} escola(s) — GPS não disponível`,
      });
    }

    return pts;
  }, [escolas, cidade]);

  const temMapa = mapPoints.length > 0;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      {/* Header */}
      <div>
        <div style={{ fontSize:12,fontWeight:700,color:'#7c3aed',letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:10 }}>
          Etapa 4 de 4 — Resumo Final
        </div>
        <h1 style={{ fontSize:32,fontWeight:900,color:'#fff',letterSpacing:'-0.03em',lineHeight:1.1,margin:0 }}>
          {escolas.length} escola{escolas.length !== 1 ? 's' : ''} em {cidade.nome}
        </h1>
        <p style={{ fontSize:13,color:'#64748b',marginTop:6,lineHeight:1.5 }}>
          {escolas.map(e => (e?.nome || '').replace('[DEMO] ','')).join(' · ')}
        </p>
      </div>

      {/* Mapa grande */}
      {temMapa ? (
        <div style={{
          borderRadius:16, overflow:'hidden',
          border:'1px solid rgba(124,58,237,0.25)',
          height:340,
          boxShadow:'0 8px 32px rgba(0,0,0,0.5)',
        }}>
          <UnifiedMapView
            dataPoints={mapPoints}
            style={{ height:'100%' }}
            config={{
              initialZoom: 13,
              useClusteringByDefault: false,
              enableToolbar: false,
              theme: 'dark',
            }}
          />
        </div>
      ) : (
        /* Fallback: iframe OpenStreetMap quando não há dados Leaflet */
        cidade.lat ? (
          <div style={{ borderRadius:16,overflow:'hidden',border:'1px solid rgba(255,255,255,0.08)',height:300,position:'relative' }}>
            <iframe
              title="Mapa do município"
              style={{ width:'100%',height:'100%',border:'none',display:'block',filter:'hue-rotate(220deg) saturate(0.4) brightness(0.6)' }}
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${cidade.lng!-0.18},${cidade.lat!-0.12},${cidade.lng!+0.18},${cidade.lat!+0.12}&layer=mapnik&marker=${cidade.lat},${cidade.lng}`}
              loading="lazy"
            />
            <div style={{ position:'absolute',bottom:0,left:0,right:0,padding:'10px 14px',background:'linear-gradient(transparent,rgba(0,0,0,0.8))' }}>
              <div style={{ fontSize:13,fontWeight:700,color:'#e2e8f0' }}>{cidade.nome}, {cidade.uf}</div>
            </div>
          </div>
        ) : null
      )}

      {/* Grade de totalizadores */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
        {items.map(item => (
          <div key={item.label} style={{
            background:'rgba(255,255,255,0.03)',
            border:'1px solid rgba(255,255,255,0.07)',
            borderRadius:14, padding:'14px 16px',
            position:'relative', overflow:'hidden',
          }}>
            <div style={{ position:'absolute',left:0,top:'15%',bottom:'15%',width:3,borderRadius:'0 3px 3px 0',background:item.color,opacity:.85 }}/>
            <div style={{ fontSize:28,fontWeight:900,color:'#f1f5f9',letterSpacing:'-0.02em',lineHeight:1 }}>
              {item.value.toLocaleString('pt-BR')}
            </div>
            <div style={{ fontSize:10,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.08em',marginTop:5 }}>
              {item.icon} {item.label}
            </div>
          </div>
        ))}
      </div>

      {/* Botão launch */}
      <button
        onClick={onLaunch}
        style={{
          width:'100%', padding:'18px', borderRadius:16, border:'none', cursor:'pointer',
          background:'linear-gradient(135deg,#7c3aed,#4f46e5)', color:'#fff', fontSize:16, fontWeight:800,
          boxShadow:'0 12px 32px rgba(124,58,237,0.45)', transition:'transform 0.15s,box-shadow 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 18px 40px rgba(124,58,237,0.55)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 12px 32px rgba(124,58,237,0.45)'; }}
      >
        🚀 Abrir Simulation Studio
      </button>
    </div>
  );
}
